import { useMergeRefs } from '@dni-react-hooks/use-merge-refs'
import * as stylex from '@stylexjs/stylex'
import { useEffect, useRef, type ComponentPropsWithRef, type ForwardedRef, type MouseEventHandler, type PropsWithChildren } from 'react'
import { Backdrop } from '../Backdrop/Backdrop'
import { Fade } from '../Fade/Fade'
import { Portal } from '../Portal/Portal'
import { zIndex } from '../tokens/zIndex.stylex'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'
import { modalScrollLockHelper } from './modalScrollLockHelper'

const _baseStyles = stylex.create({
  root: {
    position: 'fixed',
    zIndex: zIndex.modal,
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
  },
})

export interface ModalProps extends ComponentPropsWithRef<'div'> {
  /**
   * 根元素的 ref
   */
  rootRef?: ForwardedRef<HTMLDivElement>
  /**
   * 根元素的自訂樣式
   */
  rootStyle?: stylex.StyleXStyles
  /**
   * 自訂內部子元素樣式覆寫
   */
  xStyles?: Partial<XStyles<keyof typeof _baseStyles>>
  /**
   * 是否開啟 Modal
   */
  open?: boolean
  /**
   * 點擊背景或關閉區域時的回呼
   */
  onClose?: MouseEventHandler<HTMLDivElement>
  /**
   * 是否停用 Portal，直接在原位渲染
   * @default false
   */
  disablePortal?: boolean
  /**
   * 是否隱藏背景遮罩
   * @default false
   */
  hideBackdrop?: boolean
  /**
   * 指定點擊關閉的判定區域元素
   */
  closeAreaElement?: HTMLElement | null
  /**
   * 是否停用滾動鎖定
   * @default false
   */
  disableScrollLock?: boolean
  /**
   * 退場動畫結束時的回呼
   */
  onExited?: () => void
}

const {
  modalUpdate,
  modalUnmount,
} = modalScrollLockHelper()

/**
 * Modal 模態視窗元件，提供全螢幕覆蓋層與背景遮罩。
 * 支援 Portal 渲染、滾動鎖定與淡入淡出動畫。
 */
export const Modal = (props: PropsWithChildren<ModalProps>) => {
  const { rootRef: rootRefProp, rootStyle, xStyles, open, disablePortal, hideBackdrop, onClick, onClose, onExited, children, closeAreaElement, disableScrollLock, ...rest } = props
  const baseStyles = useXStyles(_baseStyles, xStyles)
  const modalRef = useRef<HTMLDivElement>(null)
  const mergeRef = useMergeRefs(rootRefProp, modalRef)
  const backdropRef = useRef<HTMLDivElement>(null)
  const closeAreaClick = useRef<boolean | null>(false)

  const handleMouseDown: MouseEventHandler<HTMLDivElement> = (event) => {
    /**
     * We don't want to close the dialog when clicking the dialog content.
     * Make sure the event starts and ends on the same DOM element.
     */
    if (closeAreaElement) {
      closeAreaClick.current = event.target === closeAreaElement
    }
    else {
      closeAreaClick.current = event.target === event.currentTarget || event.target === backdropRef.current
    }
  }

  useEffect(() => {
    const modal = modalRef.current

    if (modal && open !== undefined) {
      modalUpdate(modal, open, disableScrollLock)
    }
    return () => {
      if (modal) {
        modalUnmount(modal)
      }
    }
  }, [open, disableScrollLock])

  const handleClick: ModalProps['onClick'] = (event) => {
    if (onClick) {
      onClick(event)
    }

    /**
     * Ignore the events not coming from the "closeArea".
     */
    if (!closeAreaClick.current) {
      return
    }

    closeAreaClick.current = null

    onClose?.(event)
  }

  return (
    <Portal disablePortal={disablePortal}>
      <Fade
        rootRef={mergeRef}
        in={open}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onExited={onExited}
        rootStyle={[
          baseStyles.root,
          rootStyle,
        ]}
        {...rest}
      >
        {!hideBackdrop ? <Backdrop rootRef={backdropRef} open data-testid="modal-backdrop" /> : null}
        {children}
      </Fade>
    </Portal>
  )
}
